import React, { useState, useEffect, useRef, useMemo } from 'react';

// Small shared building blocks for the Catalogue Data design system.

export const prefersReducedMotion = () => {
  try { return window.matchMedia('(prefers-reduced-motion: reduce)').matches; } catch (e) { return false; }
};

// Animate a number from 0 to `target` (easeOutCubic). Returns the live value;
// the caller formats it. Honours prefers-reduced-motion.
export const useCountUp = (target, duration = 900) => {
  const [val, setVal] = useState(typeof target === 'number' ? 0 : target);
  const didAnimate = useRef(false);
  const raf = useRef();
  useEffect(() => {
    if (typeof target !== 'number' || !isFinite(target)) { setVal(target); return undefined; }
    // Count up ONCE, the first time a real (non-zero) value is ready. Later
    // changes (async real data loading in, date-range tweaks) just snap to the
    // new value, so the number never gets stuck mid-tween or flickers to zero.
    if (didAnimate.current || prefersReducedMotion() || target === 0) { setVal(target); return undefined; }
    didAnimate.current = true;
    let start = null;
    const tick = (now) => {
      if (start === null) start = now;
      const t = Math.min(1, (now - start) / duration);
      const eased = 1 - Math.pow(1 - t, 3);
      setVal(target * eased);
      if (t < 1) raf.current = requestAnimationFrame(tick);
      else setVal(target);
    };
    raf.current = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf.current);
  }, [target, duration]);
  return val;
};

// ── Inline icons ──────────────────────────────────────────────
const svg = (paths, extra = null) => (props) => (
  <svg width={props.size || 18} height={props.size || 18} viewBox="0 0 24 24" fill="none"
    stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
    {paths}{extra}
  </svg>
);
export const BulbIcon = svg(<><path d="M9 18h6M10 22h4" /><path d="M12 2a7 7 0 0 0-4 12.7c.6.5 1 1.3 1 2.1V18h6v-1.2c0-.8.4-1.6 1-2.1A7 7 0 0 0 12 2z" /></>);
export const WarningIcon = svg(<><path d="M12 9v4M12 17h.01" /><path d="M10.3 3.9 1.8 18a2 2 0 0 0 1.7 3h17a2 2 0 0 0 1.7-3L13.7 3.9a2 2 0 0 0-3.4 0z" /></>);
export const TargetIcon = svg(<><circle cx="12" cy="12" r="9" /><circle cx="12" cy="12" r="5" /><circle cx="12" cy="12" r="1.4" fill="currentColor" stroke="none" /></>);
export const TrendUpIcon = svg(<><path d="M22 7l-8.5 8.5-5-5L2 17" /><path d="M16 7h6v6" /></>);
export const TrendDownIcon = svg(<><path d="M22 17l-8.5-8.5-5 5L2 7" /><path d="M16 17h6v-6" /></>);
export const ArrowRightIcon = svg(<path d="M5 12h14M13 6l6 6-6 6" />);
export const OPP_ICONS = { bulb: BulbIcon, warning: WarningIcon, target: TargetIcon, up: TrendUpIcon, down: TrendDownIcon };

// ── TrendPill ─────────────────────────────────────────────────
export const TrendPill = ({ value, goodWhenUp = true, suffix = '%' }) => {
  if (value == null || Number.isNaN(value)) return null;
  const up = value > 0;
  const flat = value === 0;
  const good = flat ? null : (up === goodWhenUp);
  const cls = flat ? 'cd-trend--flat' : (good ? 'cd-trend--up' : 'cd-trend--down');
  const arrow = flat ? '' : (up ? '▲' : '▼');
  return <span className={`cd-trend ${cls}`}>{arrow} {Math.abs(value)}{suffix}</span>;
};

// ── Medal ─────────────────────────────────────────────────────
export const Medal = ({ rank }) => (
  <span className={`cd-medal ${rank <= 3 ? `cd-medal--${rank}` : ''}`}>{rank}</span>
);

// ── Avatar ────────────────────────────────────────────────────
const AVATAR_COLORS = ['#007EA0', '#6366F1', '#0EA5A4', '#D9921A', '#DB6FB0', '#5B8DEF', '#16A34A', '#E5484D'];
const initials = (name = '') => name.trim().split(/\s+/).map((w) => w[0]).slice(0, 2).join('').toUpperCase() || '?';
const colorFor = (name = '') => {
  let h = 0;
  for (let i = 0; i < name.length; i++) h = (h * 31 + name.charCodeAt(i)) % 997;
  return AVATAR_COLORS[h % AVATAR_COLORS.length];
};
export const Avatar = ({ name, size = 'md' }) => (
  <span className={`cd-avatar cd-avatar--${size}`} style={{ background: colorFor(name) }} aria-hidden>{initials(name)}</span>
);

// ── Sparkline ─────────────────────────────────────────────────
export const Sparkline = ({ data = [], width = 72, height = 26, draw = false, strokeWidth = 2 }) => {
  const { pts, dot } = useMemo(() => {
    if (!data || data.length === 0) return { pts: '', dot: null };
    const max = Math.max(...data); const min = Math.min(...data);
    const range = max - min || 1; const n = data.length;
    const coords = data.map((d, i) => {
      const x = n === 1 ? width : (i / (n - 1)) * width;
      const y = height - ((d - min) / range) * (height - 5) - 2.5;
      return [x, y];
    });
    return { pts: coords.map((c) => `${c[0].toFixed(1)},${c[1].toFixed(1)}`).join(' '), dot: coords[coords.length - 1] };
  }, [data, width, height]);
  const len = useMemo(() => {
    const a = pts ? pts.split(' ').map((p) => p.split(',').map(Number)) : [];
    let l = 0; for (let i = 1; i < a.length; i++) l += Math.hypot(a[i][0] - a[i - 1][0], a[i][1] - a[i - 1][1]);
    return Math.ceil(l) || 1;
  }, [pts]);
  if (!pts) return null;
  return (
    <svg className="cd-spark" width={width} height={height} viewBox={`0 0 ${width} ${height}`}>
      <polyline className={`cd-spark-line ${draw ? 'is-draw' : ''}`} points={pts} strokeWidth={strokeWidth}
        style={draw ? { '--cd-spark-len': len } : undefined} />
      {dot && <circle className="cd-spark-dot" cx={dot[0]} cy={dot[1]} r="2.3" />}
    </svg>
  );
};

// ── SectionHeader ─────────────────────────────────────────────
export const SectionHeader = ({ title, subtitle, action }) => (
  <div className="cd-sec">
    <div className="cd-sec-head">
      <h3 className="cd-sec-title">{title}</h3>
      {subtitle && <div className="cd-sec-subtitle">{subtitle}</div>}
    </div>
    {action && (
      <button type="button" className="cd-sec-action" onClick={action.onClick}>
        {action.label} <ArrowRightIcon size={14} />
      </button>
    )}
  </div>
);

// A plain-English label for what the ▲/▼ change compares against, driven by the
// selected date range, e.g. "vs the previous 12 months".
export const comparisonLabel = (preset) => {
  switch (preset) {
    case 'today': return 'vs yesterday';
    case 'yesterday': return 'vs the previous day';
    case 'last7': return 'vs the previous 7 days';
    case 'last4weeks': return 'vs the previous 4 weeks';
    case 'last6months': return 'vs the previous 6 months';
    case 'last12months': return 'vs the previous 12 months';
    case 'mtd': return 'vs the previous month';
    case 'ytd': return 'vs the previous year';
    default: return 'vs the previous period';
  }
};
